/** Small DOM helpers. No framework: the app is a few dozen screens, and a
 *  framework would be more code than the screens. */

type Child = Node | string | number | null | undefined | false;
type Attrs = Record<string, unknown>;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K, attrs?: Attrs | null, ...kids: (Child | Child[])[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (v === null || v === undefined || v === false) continue;
      if (k === 'class') node.className = String(v);
      else if (k === 'html') node.innerHTML = String(v);
      else if (k === 'text') node.textContent = String(v);
      else if (k === 'style') node.setAttribute('style', String(v));
      else if (k.startsWith('on') && typeof v === 'function') {
        node.addEventListener(k.slice(2), v as EventListener);
      } else node.setAttribute(k, v === true ? '' : String(v));
    }
  }
  for (const kid of kids.flat()) {
    if (kid === null || kid === undefined || kid === false) continue;
    node.append(typeof kid === 'object' ? kid : document.createTextNode(String(kid)));
  }
  return node;
}

export function clear(node: HTMLElement): HTMLElement {
  while (node.firstChild) node.removeChild(node.firstChild);
  return node;
}

export function toast(message: string, kind: 'ok' | 'crit' | 'info' = 'info'): void {
  const host = document.getElementById('toasts');
  if (!host) return;
  const t = el('div', { class: `toast ${kind}`, role: 'status' }, message);
  host.append(t);
  setTimeout(() => {
    t.style.opacity = '0';
    setTimeout(() => t.remove(), 220);
  }, kind === 'crit' ? 6000 : 3400);
}

export function field(label: string, input: HTMLElement, hint?: string): HTMLElement {
  const id = `f_${Math.random().toString(36).slice(2, 8)}`;
  input.setAttribute('id', id);
  return el('label', { class: 'field', for: id },
    el('span', { text: label }),
    input,
    hint ? el('small', { text: hint }) : null);
}

export function input(attrs: Attrs = {}): HTMLInputElement {
  return el('input', { class: 'inp', ...attrs });
}

export function select(options: Array<[string, string]>, value?: string, attrs: Attrs = {}): HTMLSelectElement {
  const s = el('select', { class: 'inp', ...attrs });
  for (const [v, label] of options) {
    s.append(el('option', { value: v, selected: v === value }, label));
  }
  if (value !== undefined) s.value = value;
  return s;
}

/**
 * An ordinary button. `type="button"` on purpose: a bare `<button>` defaults to
 * `type="submit"`, so one dropped inside a form would submit it by accident.
 * A button made here does what its `onclick` says and nothing else.
 *
 * For the button that submits a form, use `submitButton` below.
 */
export function button(label: string, kind = '', onclick?: () => void): HTMLButtonElement {
  return el('button', { class: `btn ${kind}`, type: 'button', onclick }, label);
}

/**
 * The button that submits a form.
 *
 * This exists because its absence broke every form in the application at once.
 * Sign-in, create-agency, accept-invite, the two-factor challenge and both
 * onboarding forms built their submit control with `button()`, which hard-codes
 * `type="button"`. A `type="button"` inside a `<form>` does not submit it, and
 * a form that is never submitted never fires `onsubmit` — so every one of those
 * handlers was unreachable. Clicking did nothing whatsoever: no request, no
 * error, no spinner, no sign that anything had been pressed.
 *
 * The forms were right. The buttons were right on their own. Only the
 * combination was wrong, which is exactly why nothing caught it.
 */
export function submitButton(label: string, kind = ''): HTMLButtonElement {
  return el('button', { class: `btn ${kind}`, type: 'submit' }, label);
}

export function chip(label: string, kind = ''): HTMLElement {
  return el('span', { class: `chip ${kind}` }, label);
}

/**
 * Deliberate markup, in a place that otherwise takes text.
 *
 * WHY THIS EXISTS, AND THE ONE RULE FOR USING IT
 * `table()` and `row()` used to put a string cell through `innerHTML`. That is
 * fine for markup written here in the source and catastrophic for anything a
 * person typed — and the two were indistinguishable at the call site. An audit
 * on 5 September 2026 found twenty-three places interpolating user-supplied
 * values into those string cells: lead names on the busiest screen in the
 * product, carrier and team and producer names, agent notes, licence numbers,
 * audit-log actor emails, and — worst, because the attacker needs no account
 * at all — the free-text message on a join request, rendered in the team
 * leader's browser, and the import reject messages, which quote the offending
 * cell of the uploaded file verbatim.
 *
 * So strings are TEXT everywhere now, and markup has to say so. Which leaves
 * exactly one rule:
 *
 *   **Never pass a value to `raw()`. Only a literal written in this repo.**
 *
 * If any part of the string is data, build a Node instead — `mono()`,
 * `muted()`, `chip()`, or `el('span', { text })`. `ui.raw.test.ts` fails if a
 * call to `raw()` contains an interpolation, so this is enforced rather than
 * merely asked for.
 */
export function raw(html: string): HTMLElement {
  const span = el('span');
  span.innerHTML = html;
  return span;
}

/** A figure in the tabular-numerals face. Takes text, always safe. */
export function mono(s: string): HTMLElement {
  return el('span', { class: 'mono', text: s });
}

/** Absence, in the colour the rest of the app uses for it. */
export function muted(s: string): HTMLElement {
  return el('span', { style: 'color:var(--ink-4)', text: s });
}

export function tile(k: string, v: string, d?: string, accent = 'var(--aqua)'): HTMLElement {
  return el('div', { class: 'tile', style: `--accent:${accent}` },
    el('div', { class: 'k', text: k }),
    el('div', { class: 'v', text: v }),
    d ? el('div', { class: 'd', text: d }) : null);
}

export function panel(title: string | null, ...kids: (Child | Child[])[]): HTMLElement {
  return el('section', { class: 'panel' }, title ? el('h2', { text: title }) : null, ...kids);
}

export function row(opts: {
  lead?: Node; title: string | Node; sub?: string | Node; right?: Node | Node[]; onclick?: () => void;
}): HTMLElement {
  const r = el('div', { class: `row${opts.onclick ? ' tap' : ''}`, ...(opts.onclick ? { role: 'button', tabindex: '0' } : {}) });
  if (opts.lead) r.append(opts.lead);
  const main = el('div', { class: 'main' });
  // `text`, never `html`. A row title is a lead's name far more often than it
  // is markup, and a name is typed by a person. Pass `raw()` for real markup.
  main.append(typeof opts.title === 'string' ? el('div', { class: 't', text: opts.title }) : opts.title);
  if (opts.sub) main.append(typeof opts.sub === 'string' ? el('div', { class: 's', text: opts.sub }) : opts.sub);
  r.append(main);
  if (opts.right) r.append(el('div', { class: 'r' }, ...(Array.isArray(opts.right) ? opts.right : [opts.right])));
  if (opts.onclick) {
    r.addEventListener('click', opts.onclick);
    r.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') {
        e.preventDefault();
        opts.onclick!();
      }
    });
  }
  return r;
}

export function ring(score: number): HTMLElement {
  const size = 38;
  const r = (size - 5) / 2;
  const c = 2 * Math.PI * r;
  const cls = score >= 75 ? 'hi' : score >= 50 ? '' : score >= 30 ? 'mid' : 'lo';
  const wrap = el('div', { class: `ring ${cls}`, role: 'img', 'aria-label': `Lead score ${score} out of 100` });
  wrap.innerHTML = `<svg viewBox="0 0 ${size} ${size}" aria-hidden="true">
    <circle class="bg" cx="${size / 2}" cy="${size / 2}" r="${r}"/>
    <circle class="fg" cx="${size / 2}" cy="${size / 2}" r="${r}"
      stroke-dasharray="${c.toFixed(2)}" stroke-dashoffset="${(c * (1 - score / 100)).toFixed(2)}"/>
  </svg><b>${score}</b>`;
  return wrap;
}

export function empty(title: string, blurb: string, action?: HTMLElement): HTMLElement {
  return el('div', { class: 'empty' },
    el('h3', { text: title }),
    el('p', { text: blurb }),
    action ?? null);
}

export function skeleton(rows = 4): HTMLElement {
  const w = el('div', { class: 'rows', 'aria-busy': 'true', 'aria-label': 'Loading' });
  for (let i = 0; i < rows; i += 1) {
    w.append(el('div', { class: 'row' },
      el('div', { class: 'skeleton', style: `width:${40 + (i % 3) * 18}%` })));
  }
  return w;
}

export function table(head: string[], body: Array<{ cells: (string | Node)[]; onclick?: () => void }>): HTMLElement {
  const t = el('table', { class: 'data' });
  const hr = el('tr');
  for (const h of head) hr.append(el('th', { text: h }));
  t.append(el('thead', null, hr));
  const tb = el('tbody');
  for (const r of body) {
    const tr = el('tr', r.onclick ? { class: 'tap' } : null);
    for (const cell of r.cells) {
      const td = el('td');
      // Text, never markup — see `raw()` above for why, and for how to opt in
      // on the handful of cells that genuinely are markup.
      if (typeof cell === 'string') td.textContent = cell;
      else td.append(cell);
      tr.append(td);
    }
    if (r.onclick) tr.addEventListener('click', r.onclick);
    tb.append(tr);
  }
  t.append(tb);
  return el('div', { class: 'xscroll' }, t);
}

/**
 * A modal drawer.
 *
 * It lives on `document.body` rather than inside `#app`, which is what lets it
 * sit above everything — and is also why it has to clean up after itself. The
 * router replaces the contents of `#app` on every navigation and never touches
 * the body, so a drawer left open when somebody pressed Back stayed on screen,
 * floating over a completely different page, with `overflow: hidden` still
 * pinning the scroll. Escape closed it. Back did not. Hence the `hashchange`
 * listener.
 *
 * Every listener it adds is removed on close, including the ones that do the
 * closing. A dismissed drawer still listening for Escape will cheerfully try to
 * close itself a second time, and leaks one handler per drawer opened across a
 * long session.
 */
export function drawer(title: string, body: HTMLElement): () => void {
  const scrim = el('div', { class: 'scrim' });
  let open = true;

  const close = (): void => {
    if (!open) return;
    open = false;
    scrim.remove();
    panelEl.remove();
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKey);
    window.removeEventListener('hashchange', close);
  };

  const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(); };

  const panelEl = el('aside', { class: 'drawer', role: 'dialog', 'aria-modal': 'true', 'aria-label': title },
    el('header', null,
      el('h2', { text: title }),
      button('Close', 'sm ghost', close)),
    el('div', { class: 'body' }, body));

  scrim.addEventListener('click', close);
  document.addEventListener('keydown', onKey);
  window.addEventListener('hashchange', close);
  document.body.append(scrim, panelEl);
  document.body.style.overflow = 'hidden';
  return close;
}

export function fmtPhone(p: string | null): string {
  if (!p || p.length !== 10) return p ?? '—';
  return `(${p.slice(0, 3)}) ${p.slice(3, 6)}-${p.slice(6)}`;
}

export function fmtHour(h: number | null): string {
  if (h === null) return '—';
  const H = Math.floor(h) % 24;
  const m = Math.round((h % 1) * 60);
  const ap = H < 12 ? 'am' : 'pm';
  const hh = H % 12 === 0 ? 12 : H % 12;
  return `${hh}${m ? `:${String(m).padStart(2, '0')}` : ''}${ap}`;
}

export function fmtMoney(cents: number | null): string {
  if (cents === null) return '—';
  return `$${Math.round(cents / 100).toLocaleString('en-US')}`;
}

export function ago(iso: string | null): string {
  if (!iso) return 'never';
  const d = Date.now() - new Date(iso).getTime();
  if (d < 60_000) return 'just now';
  if (d < 3_600_000) return `${Math.floor(d / 60_000)}m ago`;
  if (d < 86_400_000) return `${Math.floor(d / 3_600_000)}h ago`;
  if (d < 2_592_000_000) return `${Math.floor(d / 86_400_000)}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export const STATUS_LABEL: Record<string, string> = {
  new: 'New', attempting: 'Attempting contact', no_answer: 'No answer', busy: 'Busy',
  voicemail: 'Voicemail', wrong_number: 'Wrong number', disconnected: 'Disconnected',
  contacted: 'Contacted', qualified: 'Qualified', unqualified: 'Unqualified',
  callback: 'Callback requested', appointment_set: 'Appointment set',
  appointment_completed: 'Appointment completed', application_started: 'Application started',
  application_submitted: 'Application submitted', approved: 'Approved', issued: 'Issued',
  sold: 'Sold', not_interested: 'Not interested', do_not_contact: 'Do not contact',
  duplicate: 'Duplicate', invalid: 'Invalid', closed_lost: 'Closed / lost',
};
